import React, { useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CLoadingButton,
  CMultiSelect,
  CRow,
  CTooltip,
} from '@coreui/react-pro'
import moment from 'moment'
import Bootbox from 'bootbox-react'
import { cilArrowLeft, cilInfo, cilList, cilReload } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import { useFormik } from 'formik'
import ToastComponent from 'src/components/common/TaostComponent'
import ScorecardService from 'src/service/ScorecardService'
import { Link, useNavigate } from 'react-router-dom'
import Loader from 'src/components/Loader'
import ScorecardGrid from './ScorecardGrid'
import { INDOOR_COLUMNS, hasScores } from './scorecardConfig'
import './ScoreCard.css'

const IndoorScorecard = (props) => {
  const navigate = useNavigate()
  const [indoorScorecardLoading, setIndoorScorecardLoading] = useState(false)
  const formik = useFormik({
    initialValues: {
      data: props.scorecardDetails,
      scorcard_link_name: props.fixtureDetails?.scorcard_link_name,
      scorcard_link_url: props.fixtureDetails?.scorcard_link_url,
      fall_of_wickets: props.fixtureDetails?.fall_of_wickets,
      match_report: props.fixtureDetails?.match_report,
      potm_match_status: props.fixtureDetails?.potm_match_status,
      match_status: '',
    },
    enableReinitialize: true,
    onSubmit: (data, actions) => {
      setIndoorScorecardLoading(true)
      data.fixtureId = props.fixtureId
      data.potm = props.selectedValue
      data.status = props.statusValue
      props.setLoader(false)
      ScorecardService.saveIndoorScorecard(data).then((res) => {
        if (res.status === 200) {
          setIndoorScorecardLoading(false)
          props.fetchData()
          ToastComponent(res.message, 'success')
        }
      })
    },
  })

  const [commonScorecardLoading, setCommonScorecardLoading] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const handleResetConfirm = () => {
    resetFixtureScorecard()
    return setShowResetConfirm(false)
  }
  const handleResetCancel = () => {
    return setShowResetConfirm(false)
  }
  const clearScorecard = () => {
    setShowResetConfirm(true)
  }
  const resetFixtureScorecard = () => {
    const data = {}
    data.fixtureId = props.fixtureId
    data.inning = props.defaultInning
    ScorecardService.clearFixtureScorecard(data).then((res) => {
      if (res.status === 200) {
        setCommonScorecardLoading(false)
        props.fetchData()
        ToastComponent(res.message, 'success')
      }
    })
  }

  // ---- presentation helpers ------------------------------------------------

  const teamId = props.teamId ?? props.param?.teamId
  // Indoor screens used to hardcode their 33 columns; fall back to that list so
  // the grid can never come up with nothing to type into.
  const columns = props.scorecardColoumns?.length ? props.scorecardColoumns : INDOOR_COLUMNS

  const enteredCount = (props.scorecardDetails || []).filter((item) =>
    hasScores(item, columns),
  ).length
  const playerCount = (props.scorecardDetails || []).length
  const enteredPercent = playerCount ? Math.round((enteredCount / playerCount) * 100) : 0

  const scorecardTitle = props.fixtureDetails.scorcard_link_url ? (
    <Link to={props.fixtureDetails.scorcard_link_url} target="_blank">
      Player stats
    </Link>
  ) : (
    'Player stats'
  )

  const inningsSelect = props.fixtureDetails?.match_type === '2-Day' && (
    <div className="d-flex align-items-center gap-2">
      <CFormLabel htmlFor="inning" className="mb-0 small fw-semibold text-medium-emphasis">
        Innings
      </CFormLabel>
      <CFormSelect
        aria-label="select inning"
        name="inning"
        onChange={props.changeInning}
        value={props.defaultInning}
        id="inning"
        style={{ width: 150 }}
      >
        <option value={1}>1st Innings</option>
        <option value={2}>2nd Innings</option>
      </CFormSelect>
    </div>
  )

  const matchStatusCard = (
    <CCard className="mt-3">
      <CCardHeader>
        <strong>Match status</strong>
        <div className="text-medium-emphasis small">
          The last step - set this once the scores above are saved.
        </div>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md={6}>
            <div className="d-flex align-items-center gap-2">
              <CFormSelect
                aria-label="Select Structure"
                name="match_status"
                value={props.statusValue}
                id="match_status"
                onChange={props.handleMatchStatus}
              >
                <option value={0}>Select Match Status </option>
                <option value={1}>In-Progress</option>
                <option value={2}>Completed</option>
              </CFormSelect>
              <CTooltip content="What the match statuses mean">
                <CButton
                  color="secondary"
                  variant="ghost"
                  onClick={() => props.setVisible(!props.visible)}
                  aria-label="Match status help"
                >
                  <CIcon icon={cilInfo} />
                </CButton>
              </CTooltip>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  )

  return indoorScorecardLoading ? (
    <Loader />
  ) : (
    <>
      <CForm onSubmit={formik.handleSubmit}>
        <div className="ge-page-header">
          <button type="button" className="ge-back-link" onClick={() => navigate(-1)}>
            <CIcon icon={cilArrowLeft} />
            Manage Scores
          </button>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <h1 className="ge-page-title">
                Indoor scorecard &mdash; {props.fixtureDetails.team_name}
              </h1>
              <p className="ge-page-subtitle">
                Enter each player&rsquo;s stats for this innings. Fantasy points are recalculated
                when you save.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <CButton
                color="secondary"
                variant="outline"
                href={`/manage-scores/team-players/${props.fixtureId}/${teamId}`}
              >
                <CIcon icon={cilList} className="me-1" />
                Squad
              </CButton>
              <CButton color="secondary" variant="outline" onClick={props.handleInfoClick}>
                <CIcon icon={cilInfo} className="me-1" />
                Column legend
              </CButton>
            </div>
          </div>
        </div>

        <div className="ge-meta-strip">
          <div className="ge-meta-item">
            <span className="ge-meta-label">Round</span>
            <span className="ge-meta-value">{props.fixtureDetails.round}</span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Team</span>
            <span className="ge-meta-value">{props.fixtureDetails.team_name}</span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Starts</span>
            <span className="ge-meta-value">
              {moment(props.fixtureDetails.start_date).format('D MMM YYYY')}
              {', '}
              {moment(props.fixtureDetails.start_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
          <div className="ge-meta-item">
            <span className="ge-meta-label">Ends</span>
            <span className="ge-meta-value">
              {moment(props.fixtureDetails.end_date).format('D MMM YYYY')}
              {', '}
              {moment(props.fixtureDetails.end_time, ['HH:mm']).format('h:mma')}
            </span>
          </div>
        </div>

        <div className="sc-toolbar">
          <div className="d-flex flex-wrap align-items-center gap-3">{inningsSelect}</div>
          <div className="d-flex align-items-center gap-2">
            <span className="small fw-medium text-medium-emphasis">
              {enteredCount} of {playerCount} players have scores saved
            </span>
            <div className="sc-progress-track">
              <div className="sc-progress-fill" style={{ width: `${enteredPercent}%` }} />
            </div>
          </div>
        </div>

        <CCard className="mb-4">
          <CCardHeader>
            <strong>{scorecardTitle}</strong>
          </CCardHeader>
          <CCardBody>
            {props.loader || props.isLoading ? (
              <Loader />
            ) : (
              <ScorecardGrid
                columns={columns}
                rows={props.scorecardDetails}
                isMobile={props.isMobile}
                onChange={formik.handleChange}
                formatFantasyPoints={props.formatFantasyPoints}
              />
            )}
          </CCardBody>
        </CCard>

        <CRow className="g-3">
          <CCol md={6}>
            <CCard className="h-100">
              <CCardHeader>
                <strong>Scorecard Details</strong>
              </CCardHeader>
              <CCardBody>
                <CRow className="g-3">
                  <CCol md={6}>
                    <CFormLabel htmlFor="scorcard_link_name">Scorecard Source</CFormLabel>
                    <input
                      type="text"
                      name="scorcard_link_name"
                      className={'form-control'}
                      id="scorcard_link_name"
                      placeholder="Scorecard Source"
                      defaultValue={formik.values.scorcard_link_name}
                      onChange={(e) => props.handleInputChange(e, 'scorcard_link_name')}
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel htmlFor="scorcard_link_url">Scorecard Link</CFormLabel>
                    <input
                      type="text"
                      name="scorcard_link_url"
                      className={'form-control'}
                      id="scorcard_link_url"
                      placeholder="Scorecard Link"
                      defaultValue={formik.values.scorcard_link_url}
                      onChange={(e) => props.handleInputChange(e, 'scorcard_link_url')}
                    />
                  </CCol>
                  {/* <CCol xs={12}>
                    <CFormLabel htmlFor="fall_of_wickets">Fall of Wickets</CFormLabel>
                    <CFormTextarea
                      id="fall_of_wickets"
                      aria-label="Fall of wickets"
                      defaultValue={formik.values.fall_of_wickets}
                      name={'fall_of_wickets'}
                      onChange={(e) => props.handleInputChange(e, 'fall_of_wickets')}
                    ></CFormTextarea>
                  </CCol> */}
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol md={6}>
            <CCard className="h-100">
              <CCardHeader>
                <strong>Bonus Points</strong>
              </CCardHeader>
              <CCardBody>
                <CRow className="g-3">
                  <CCol xs={12}>
                    <CFormLabel htmlFor="player_of_the_match">Player Of The Match</CFormLabel>
                    <CMultiSelect
                      id="player_of_the_match"
                      options={props.playerList}
                      selectionType="tags"
                      name="player"
                      onChange={props.handleChange}
                      value={props.playerList.filter((obj) =>
                        props.selectedValue.includes(obj.value),
                      )}
                    />
                  </CCol>
                  <CCol xs={12}>
                    <CFormLabel htmlFor="potm_match_status">Match Result</CFormLabel>
                    <CFormSelect
                      id="potm_match_status"
                      value={formik.values.potm_match_status}
                      onChange={formik.handleChange}
                    >
                      <option>Select Match Result</option>
                      <option value="1">Won</option>
                      <option value="2">Other</option>
                    </CFormSelect>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <div className="sc-savebar sc-savebar--pinned">
          <span className="small text-medium-emphasis">
            {enteredCount} of {playerCount} players have scores saved. Saving recalculates fantasy
            points for this innings.
          </span>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {props.isMobile && (
              <CButton color="danger" variant="outline" onClick={clearScorecard}>
                <CIcon icon={cilReload} className="me-1" />
                Reset
              </CButton>
            )}
            <CLoadingButton type="submit" color="primary" loading={props.loader}>
              Save scores
            </CLoadingButton>
          </div>
        </div>

        {matchStatusCard}
      </CForm>

      <Bootbox
        show={showResetConfirm}
        type={'confirm'}
        message={'Are you sure you want to reset this?'}
        onSuccess={handleResetConfirm}
        onCancel={handleResetCancel}
        onClose={handleResetCancel}
      />
    </>
  )
}

export default IndoorScorecard
